import Immutable from "immutable";
import * as KpiRepo from "js/common/repo/backbone/kpi-repo";
import * as KpiCalculator from "js/common/kpi-calculator";
import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";

export const MAX_METRICS = 3;
export const DEFAULT_TITLE = "Top Performers";
export const DEFAULT_SECONDS_PER_LEADER = 5;

export const isEnabled = config => config.getIn(["topPerformers", "enabled"], false);

export const getTitle = config => config.getIn(["topPerformers", "title"], DEFAULT_TITLE);

// Metrics whose KPI has since been deleted are dropped rather than failing the panel.
export const getMetrics = config => config
    .getIn(["topPerformers", "metrics"], Immutable.List())
    .filter(metric => !!KpiRepo.get(metric.get("kpiId")));

// Leaderboards saved before auto-advance existed have no flag, so they keep presenting statically.
export const isAutoAdvanceEnabled = config => config.get("autoAdvance", false);

export const showsPanel = config => isEnabled(config) && !getMetrics(config).isEmpty();

const PANEL_WIDTH_FACTOR = 1.5;

// Scales rather than setting a fixed width so zoomed dashboards keep their scaled init size, and
// moves the panel left instead of letting it run off the right edge of the grid.
export const resizeLayoutForPanel = (layout, showPanel, {columns, minWidth}) => {
  const width = layout.get("width");
  const newWidth = showPanel
      ? Math.min(Math.round(width * PANEL_WIDTH_FACTOR), columns)
      : Math.max(Math.round(width / PANEL_WIDTH_FACTOR), minWidth);
  const x = Math.max(0, Math.min(layout.get("x", 0), columns - newWidth));
  return layout.set("width", newWidth).set("x", x);
};

export const getSecondsPerLeader = config => {
  const seconds = Number(config.get("secondsPerLeader"));
  return Number.isFinite(seconds) && seconds >= 1 ? Math.floor(seconds) : DEFAULT_SECONDS_PER_LEADER;
};

export const DEFAULT_LEADERS_TO_CYCLE = 10;

// An unset or blank limit, as on leaderboards saved before these options existed, means every row.
const applyLimit = (value, rowCount) => {
  const limit = Number(value);
  return Number.isFinite(limit) && limit >= 1 ? Math.min(Math.floor(limit), rowCount) : rowCount;
};

export const getLeadersToShow = (config, rowCount) => applyLimit(config.get("leadersToShow"), rowCount);

export const getCycleLength = (config, rowCount) => applyLimit(config.get("leadersToCycle"), rowCount);

export const setAutoAdvance = (config, enabled) => enabled && !config.has("leadersToCycle")
    ? config.set("autoAdvance", true).set("leadersToCycle", DEFAULT_LEADERS_TO_CYCLE)
    : config.set("autoAdvance", enabled);

export const getAutoAdvanceIndex = (elapsedSeconds, secondsPerLeader, cycleLength) => cycleLength > 0
    ? Math.floor(elapsedSeconds / secondsPerLeader) % cycleLength
    : 0;

const getTimeframeName = timeframe => timeframe.get("shortName") || timeframe.get("name");

export const getDefaultDisplayName = metric => {
  const kpi = KpiRepo.get(metric.get("kpiId"));
  const timeframe = TimeframeRepo.parse(metric.get("timeframe").toJS());
  return kpi ? `${kpi.get("name")} ${getTimeframeName(timeframe)}` : "";
};

export const getDisplayName = metric => metric.get("displayName") || getDefaultDisplayName(metric);

export const createMetric = config => Immutable.fromJS({
  kpiId: config.get("kpiId"),
  timeframe: config.get("timeframe"),
  displayName: "",
  matchAnyTagIds: [],
  matchAllTagIds: [],
  excludedTagIds: []
});

export const setEnabled = (config, enabled) => config.update("topPerformers", Immutable.Map(), topPerformers => {
  const withTitle = topPerformers.has("title") ? topPerformers : topPerformers.set("title", DEFAULT_TITLE);
  const hasMetrics = !withTitle.get("metrics", Immutable.List()).isEmpty();
  return withTitle
      .set("enabled", enabled)
      .set("metrics", enabled && !hasMetrics
          ? Immutable.List.of(createMetric(config))
          : withTitle.get("metrics", Immutable.List()));
});

const toValuesByUserId = rows => Immutable.Map(rows.map(row => [row.userId, Immutable.fromJS(row.kpi.total)]));

// Resolves to a map of userId to that user's total, or to null when the metric failed to load so
// the other metrics can still show.
export const loadMetric = (metric, requestOptions) => Promise.resolve()
    .then(() => KpiCalculator.targetedLeaderboard(metric.get("kpiId"), {
      ...requestOptions,
      timeframe: TimeframeRepo.parse(metric.get("timeframe").toJS()),
      matchAnyTagIds: metric.get("matchAnyTagIds", Immutable.List()).toArray(),
      matchAllTagIds: metric.get("matchAllTagIds", Immutable.List()).toArray(),
      excludedTagIds: metric.get("excludedTagIds", Immutable.List()).toArray()
    }))
    .then(toValuesByUserId, () => null);

// Panels remount each time their tab comes back round, so a load's metric requests are kept and
// reused until the leaderboard reloads rather than refetched on every visit.
const metricLoadsByComponentId = new Map();

// One entry per metric in getMetrics(config), each with a promise that settles once `values` is set.
export const getMetricLoads = (componentId, loadId, config, requestOptions) => {
  const existing = metricLoadsByComponentId.get(componentId);
  if (existing && existing.loadId === loadId) {
    return existing.loads;
  }
  const loads = getMetrics(config).toArray().map(metric => {
    const load = {settled: false, values: null};
    load.promise = loadMetric(metric, requestOptions).then(values => {
      load.settled = true;
      load.values = values;
    });
    return load;
  });
  metricLoadsByComponentId.set(componentId, {loadId, loads});
  return loads;
};

// A request that never settles must not hold the presentation on one leader forever.
export const MAX_METRIC_WAIT_SECONDS = 30;
